import { Link } from "react-router-dom";
import * as s from "../styles.js";

/**
 * The public privacy policy. Keep consistent with docs/privacy.md and with the
 * Chrome Web Store privacy disclosures (docs/chrome-web-store-checklist.md).
 */
export function PrivacyPage(): JSX.Element {
  const h2 = s.sectionTitle;
  return (
    <div style={{ ...s.wide, maxWidth: 860 }}>
      <h1 style={{ ...s.heading1, fontSize: 34 }}>Privacy</h1>
      <hr style={s.goldRule} />
      <div style={s.prose}>
        <p>
          EduFadder is built to help you notice education ideas without watching what you do. This page explains exactly what
          the extension and this website handle. Last updated October 2026.
        </p>

        <h2 style={h2}>The extension, while you browse</h2>
        <ul>
          <li>Automatic detection is on from installation, and the download page and welcome page say so. You can turn it off in Settings (EduFadder then reads a page only when you ask it to check that page or a selection), pause it on any site, or snooze it.</li>
          <li>Each page is first checked <strong>on your device</strong> against a short list of phrases (downloaded from EduFadder, and the same for everyone). If nothing matches, nothing is sent anywhere.</li>
          <li>If something matches, only <strong>the matching passages</strong> (short paragraphs) are sent to EduFadder's server to check what they say about the idea. The page address, page title, and the rest of the page are not sent.</li>
          <li>On search-results pages, matching happens <strong>only on your device</strong>. Your search queries and results are never sent.</li>
          <li>Text from &ldquo;Check with EduFadder&rdquo; (selected text) or the website's &ldquo;Check a passage&rdquo; is sent only because you asked.</li>
          <li>Passages are analysed in memory and are <strong>not stored or logged</strong>. They are never used to train any model.</li>
          <li>The extension stores its settings (and, if you sign in, your session) in your browser. It keeps a list of what it noticed on each open tab only until you close the browser.</li>
        </ul>

        <h2 style={h2}>What the server keeps</h2>
        <ul>
          <li><strong>If you don't have an account:</strong> no record of you. The server only increases daily totals such as &ldquo;pages checked today&rdquo;, &ldquo;times idea X was noticed today&rdquo;, and &ldquo;checks from extension version 0.2.0&rdquo; — totals with no person, address, page, or text attached. To prevent abuse, your network address is held in memory for about a minute for rate limiting, then forgotten.</li>
          <li><strong>If you create an account:</strong> your email, display name, and password (stored only as a secure hash); a count of your checks and the general topics involved (e.g. &ldquo;learning styles&rdquo;, never pages or text) for your own dashboard; which research notes you chose to open; and any feedback or suggestions you send.</li>
          <li><strong>Find research:</strong> the phrase you search for is sent from EduFadder's server to two public research catalogues, OpenAlex and ERIC (U.S. Department of Education), with nothing about you. Results are kept in the server's memory for up to a day so repeat searches are fast; the phrase is never written to the database — only a daily count of searches.</li>
          <li><strong>Web server logs</strong> record the time and the address requested on this site, without your IP address or browser details. If an error occurs, the server's error log can briefly include a network address.</li>
        </ul>

        <h2 style={h2}>Who can see what</h2>
        <ul>
          <li>Your dashboard is visible only to you.</li>
          <li>EduFadder administrators see totals only — never what any individual read or where.</li>
          <li>If you belong to an organisation using EduFadder, its administrators see only aggregate statistics, and only when enough members are active that no individual can be singled out.</li>
          <li>Research use of any data requires a separate, explicit opt-in that you can withdraw at any time. It is off unless you turn it on.</li>
        </ul>

        <h2 style={h2}>Your choices</h2>
        <ul>
          <li>Use EduFadder without an account.</li>
          <li>Pause it on any site, snooze it, hide highlights, turn off search notes, or turn automatic detection off. To remove EduFadder's access to sites completely, use chrome://extensions → EduFadder → Site access, or remove the extension.</li>
          <li>Delete your account from your <Link to="/account">account settings</Link>; your personal details are removed, and remaining curation records are anonymised.</li>
        </ul>

        <p>
          No ads, no selling of data, no third-party trackers, and no fonts or scripts loaded from other companies' servers.
          Questions? Use the <Link to="/suggest">suggestion form</Link>.
        </p>
      </div>
    </div>
  );
}
